if (!window.customElements.get('refer-a-friend')) {
  const APP_PROXY_API_PREFIX =
    window.referAFriend?.apiPrefix || (window.location.hostname.includes('staging') ? '/apps/api-hamm-1' : '/apps/api-hamm');

  const buildAppProxyUrl = (path) => `${APP_PROXY_API_PREFIX}${path}`;

  const showReferAFriendToast = (message, variant = 'success') => {
    if (!message || typeof window.showToast !== 'function') return;

    const toast = window.showToast({
      variant,
      message,
    });

    if (toast) {
      toast.style.alignSelf = 'center';
    }
  };

  class ReferAFriend extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.almostThere = this.querySelector('[data-refer-a-friend-almost-there]');
      this.main = this.querySelector('[data-refer-a-friend-main]');
      this.loading = this.querySelector('[data-refer-a-friend-loading]');
      this.resetDate = this.querySelector('[data-refer-a-friend-reset-date]');
      this.points = this.querySelector('[data-refer-a-friend-points]');
      this.uses = this.querySelector('[data-refer-a-friend-uses]');
      this.useIndicators = this.querySelectorAll('[data-refer-a-friend-use-indicator]');
      this.generatedLinks = this.querySelector('[data-refer-a-friend-generated-links]');
      this.generateLinkButtons = this.querySelectorAll('[data-refer-a-friend-generate-link-button]');
      this.linkDisclaimer = this.querySelector('[data-refer-a-friend-link-disclaimer]');
      this.emailInvites = this.querySelector('[data-refer-a-friend-email-invites]');
      this.emailInviteButtons = this.querySelectorAll('[data-refer-a-friend-email-invite-button]');
      this.divider = this.querySelector('[data-refer-a-friend-divider]');
      this.submitButton = this.querySelector('[data-refer-a-friend-submit-button]');
      this.form = this.querySelector('form');
      this.portalLink = document.querySelector('[data-portal-link]');
      this.tooltipTrigger = this.querySelector('[data-refer-a-friend-tooltip-trigger]');
      this.tooltip = this.querySelector('[data-refer-a-friend-tooltip]');
      this.customerOrderCount = Number(window.referAFriend?.orderCount || 0);
      this.mostRecentOrderDiscountCode = window.referAFriend?.mostRecentOrderDiscountCode || '';
      this.hasInactiveSubscriberTag = window.referAFriend?.hasInactiveSubscriberTag === true;
      this.useCount = 0;
      this.maxLimit = Number(window.referAFriend?.referralCap) || 5;
      this.customerEmail = window.referAFriend?.customerEmail || '';
      this.emailSubmitPending = false;
      this.isReferralFraud = this.customerOrderCount <= 1 && this.mostRecentOrderDiscountCode.includes('EGTB');

      this.generateLinkButtons.forEach((button) => button.addEventListener('click', () => this.generateLink()));
      this.emailInviteButtons.forEach((button) => button.addEventListener('click', () => this.generateEmailInput()));
      if (this.form) {
        this.form.addEventListener('submit', (event) => this.handleEmailSubmit(event));
      }
      if (this.tooltipTrigger && this.tooltip) {
        this.tooltipTrigger.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggleTooltip();
        });
        this.onDocumentClick = (event) => {
          if (!this.tooltip.contains(event.target)) this.toggleTooltip(false);
        };
        this.onDocumentKeydown = (event) => {
          if (event.key === 'Escape') this.toggleTooltip(false);
        };
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      this.validateUser();
    }

    disconnectedCallback() {
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.onDocumentKeydown) document.removeEventListener('keydown', this.onDocumentKeydown);
    }

    // Validate if the user is eligible to refer friends
    async validateUser() {
      if (this.hasInactiveSubscriberTag) {
        this.loading.classList.add('hidden');
        this.almostThere.classList.remove('hidden');
        this.main.classList.add('hidden');
        return;
      }

      try {
        const res = await fetch(buildAppProxyUrl('/antavo/activities/rewards'), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) {
          throw new Error(`HTTP error: ${res.status}`);
        }
        const { data } = await res.json();
        const isUserExisted = data?.find((item) => item.status === 'active' || item.status === 'restricted');

        if (isUserExisted) {
          //User is eligible to refer friends, fetch user data to find number of referrals left for the month
          this.getUserData();
        } else {
          this.loading.classList.add('hidden');
          this.almostThere.classList.remove('hidden');
          this.main.classList.add('hidden');
        }
      } catch (e) {
        console.error('Error validating user:', e);
        this.loading.classList.add('hidden');
        this.almostThere.classList.remove('hidden');
        this.main.classList.add('hidden');
      }
    }

    getUserData() {
      fetch(buildAppProxyUrl('/antavo/customer/detail'), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((response) => {
          //Based on referrals left build the UI to show the user
          this.buildSection(response);
        })
        .catch((error) => {
          this.loading.classList.add('hidden');
          this.almostThere.classList.remove('hidden');
          this.main.classList.add('hidden');
          console.error('Error:', error);
        });
    }

    // Generate a referral code by making a POST request to Antavo's API.
    async generateCode() {
      if (this.isReferralFraud) {
        showReferAFriendToast('Unable to send invite right now. Please try again.', 'error');
        return;
      }
      try {
        const response = await fetch(buildAppProxyUrl('/antavo/activities/rewards/claim'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        showReferAFriendToast('Unable to send invite right now. Please try again.', 'error');
        console.error('Error generating referral code:', error);
        return null;
      }
    }

    buildSection(response) {
      if (!response) return;

      this.loading.classList.add('hidden');

      // Calculate the reset date for the referral limit, which is the first of the next month
      const resetDate = this.getFirstOfNextMonth();
      const rawPoints = Number(response?.properties?.total_referral_points_earned);
      const points = Number.isFinite(rawPoints) ? rawPoints : 0;
      this.useCount = response?.properties?.monthly_referral_count || 0;

      this.almostThere.classList.add('hidden');
      this.main.classList.remove('hidden');

      if (resetDate) {
        this.resetDate.classList.remove('hidden');
        this.resetDate.innerText = `, ${window.referAFriend.strings.resetsOn} ${resetDate}`;
      }

      if (this.points) {
        this.points.innerText = `${points.toLocaleString('en-US')} pts`;
      }

      this.updateUseCounts();
    }

    toggleTooltip(force) {
      const isOpen = typeof force === 'boolean' ? force : this.tooltip.classList.contains('hidden');
      this.tooltip.classList.toggle('hidden', !isOpen);
      this.tooltipTrigger.setAttribute('aria-expanded', String(isOpen));
    }

    getFirstOfNextMonth() {
      const date = new Date();
      date.setMonth(date.getMonth() + 1, 1);
      const yyyy = date.getFullYear();
      const mm = date.getMonth();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[mm]} 1st, ${yyyy}`;
    }

    // Generate a referral link and display it in the UI when user clicks on the "Generate Link" button
    async generateLink() {
      this.generateLinkButtons.forEach((button) => button.setAttribute('disabled', true));
      const codeResponse = await this.generateCode();

      if (codeResponse && codeResponse?.coupon?.code) {
        const referralLink = `${window.location.origin}/discount/${codeResponse.coupon.code}?referral`;

        this.generatedLinks.classList.add('active');
        // Build the link and copy button
        this.generatedLinks.appendChild(this.buildLink(referralLink, codeResponse.coupon.code));
        this.linkDisclaimer?.classList.remove('hidden');

        this.generateLinkButtons.forEach((button) => {
          if (button.hasAttribute('data-primary')) {
            button.classList.add('hidden');
          } else {
            button.classList.remove('hidden');
          }
        });

        this.trackReferralSubmitted('link_generated', codeResponse.coupon.code, this.getReferralsUsed());
      }

      this.updateUseCounts();
    }

    // Generate an email input field for the user to enter a friend's email address when they click on the "Invite via Email" button
    generateEmailInput() {
      if (this.isReferralFraud) {
        showReferAFriendToast('Unable to send invite right now. Please try again.', 'error');
        return;
      }
      if (this.emailSubmitPending) return;

      const currentCount = this.getEmailInputElements().length;
      if (this.useCount + this.querySelectorAll('refer-a-friend-link').length + currentCount >= this.maxLimit) return;

      this.emailInvites.classList.add('active');
      // Build the email input field and append it to the email invites section
      this.emailInvites.appendChild(this.buildEmailInput('', true));
      // Add necessary attributes to the email input field for accessibility and form submission
      this.syncEmailFieldMetadata();
      this.updateUseCounts();
      this.updateEmailInviteVisibility();
    }

    getEmailInputElements() {
      return Array.from(this.querySelectorAll('refer-a-friend-email-input'));
    }

    syncEmailFieldMetadata() {
      this.getEmailInputElements().forEach((emailElement, idx) => {
        const input = emailElement.querySelector('input');
        if (!input) return;

        const id = `email${idx + 1}`;
        input.id = id;
        input.name = id;
        input.placeholder = window.referAFriend.strings.email || 'Email';
        input.setAttribute('aria-label', window.referAFriend.strings.email || 'Email');
      });
    }

    // Update the visibility of the email invite section and buttons based on the number of email inputs and generated links
    updateEmailInviteVisibility() {
      const emailInputCount = this.getEmailInputElements().length;
      const hasEmails = emailInputCount > 0;
      const generatedLinksCount = this.querySelectorAll('refer-a-friend-link').length;
      const canAddAnotherEmail = this.useCount + generatedLinksCount + emailInputCount < this.maxLimit;

      if (hasEmails) {
        this.emailInvites.classList.add('active');
      } else {
        this.emailInvites.classList.remove('active');
      }

      this.emailInviteButtons.forEach((button) => {
        if (button.hasAttribute('data-primary')) {
          if (hasEmails) {
            button.classList.add('hidden');
          } else {
            button.classList.remove('hidden');
          }
        } else if (hasEmails && canAddAnotherEmail) {
          button.classList.remove('hidden');
        } else {
          button.classList.add('hidden');
        }
      });

      if (this.divider) {
        if (hasEmails || this.querySelectorAll('refer-a-friend-link').length > 0) {
          this.divider.classList.remove('hidden');
        } else {
          this.divider.classList.add('hidden');
        }
      }

      if (this.submitButton) {
        if (hasEmails) {
          this.submitButton.classList.remove('hidden');
        } else {
          this.submitButton.classList.add('hidden');
        }
      }
    }

    getReferralsUsed() {
      return this.useCount + this.querySelectorAll('refer-a-friend-link').length;
    }

    // Send analytics event: Referral Submitted. Segment attaches the standard context properties automatically.
    // `email` is always the referrer (logged-in customer). `referee_email` is the friend's address and is only
    // set for email invites; downstream the invite email is sent to it.
    trackReferralSubmitted(referralType, referralCode, referralsUsed, refereeEmail) {
      if (!referralCode || typeof window.analytics?.track !== 'function') return;

      window.analytics.track('Referral Submitted', {
        referral_type: referralType,
        referral_code: referralCode,
        referral_cap: this.maxLimit,
        referrals_used: referralsUsed,
        email: this.customerEmail,
        user_type: 'Referral',
        ...(refereeEmail ? { referee_email: refereeEmail } : {}),
      });
    }

    // Ask hamm (func-klaviyo) to send "Referral Received" to Klaviyo on the friend's profile, which
    // triggers the invite email (DIGX-435). The worker checks the code belongs to this customer.
    // Fire and forget: a failure is logged but never blocks the invite flow on the page.
    sendReferralReceived(referralCode, refereeEmail) {
      if (!referralCode || !refereeEmail) return;

      fetch(buildAppProxyUrl('/klaviyo/referral-received'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refereeEmail, referralCode }),
      })
        .then((response) => {
          if (!response.ok) console.error(`Referral Received failed: HTTP ${response.status}`);
        })
        .catch((error) => console.error('Referral Received failed:', error));
    }

    trackReferralLinkCopied(referralCode) {
      if (!referralCode || typeof window.analytics?.track !== 'function') return;

      window.analytics.track('Referral Link Copied', {
        referral_code: referralCode,
        referral_cap: this.maxLimit,
        referrals_used: this.getReferralsUsed(),
      });
    }

    // Set the loading state for the email submission process, disabling/enabling buttons as needed
    setEmailSubmitLoading(isLoading) {
      this.emailSubmitPending = isLoading;

      if (this.submitButton) {
        if (isLoading) {
          this.submitButton.setAttribute('disabled', true);
        } else {
          this.submitButton.removeAttribute('disabled');
        }
      }

      this.emailInviteButtons.forEach((button) => {
        if (isLoading) {
          button.setAttribute('disabled', true);
        }
      });

      if (!isLoading) {
        this.updateUseCounts();
      }
    }

    async verifyEmails(emails) {
      try {
        const response = await fetch(buildAppProxyUrl('/shopify/customer'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ emails }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        console.error('Error verifying emails:', error);
        return null;
      }
    }

    // Handle the submission of email invites
    async handleEmailSubmit(event) {
      event.preventDefault();
      if (this.emailSubmitPending) return;

      const emailInputs = this.getEmailInputElements();
      if (!emailInputs.length) return;

      const emails = emailInputs
        .map((emailElement) => emailElement.querySelector('input'))
        .filter(Boolean)
        .map((input) => input.value.trim())
        .filter(Boolean);

      if (!emails.length) {
        if (this.form) this.form.reportValidity();
        return;
      }

      if (this.form && !this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.setEmailSubmitLoading(true);

      try {
        // Verify the emails for existing subscriptions before generating referral codes
        const response = await this.verifyEmails(emails);
        const verifiedEmails = response?.emails || [];
        if (!Array.isArray(verifiedEmails) || !verifiedEmails.length) {
          showReferAFriendToast('Unable to send invite right now. Please try again.', 'error');
          return;
        }

        const codes = await Promise.all(verifiedEmails.map(() => this.generateCode()));
        const successfulSubmissions = [];

        verifiedEmails.forEach((email, index) => {
          const code = codes[index];
          if (code?.coupon?.code) {
            successfulSubmissions.push({ email, code });
          }
        });

        const sentCount = successfulSubmissions.length;
        if (!sentCount) {
          showReferAFriendToast('Unable to send invite right now. Please try again.', 'error');
          return;
        }

        const referralsUsedBefore = this.getReferralsUsed();
        this.useCount += sentCount;
        this.emailInvites.innerHTML = '';
        this.updateEmailInviteVisibility();
        successfulSubmissions.forEach(({ code, email }, index) => {
          this.trackReferralSubmitted('email_sent', code.coupon.code, referralsUsedBefore + index + 1, email);
          this.sendReferralReceived(code.coupon.code, email);
        });

        if (sentCount > 0) {
          showReferAFriendToast(
            sentCount > 1
              ? 'Your friends will receive the email with offer link shortly in their inbox. Offer link expires in 7 days. '
              : 'Your friend will receive the email with offer link shortly in their inbox. Offer link expires in 7 days. '
          );
        }
      } catch (error) {
        showReferAFriendToast('Unable to send invite right now. Please try again.', 'error');
      } finally {
        this.setEmailSubmitLoading(false);
      }
    }

    //Referral tracker
    updateUseCounts() {
      const generatedLinks = this.querySelectorAll('refer-a-friend-link');
      const emailInputs = this.querySelectorAll('refer-a-friend-email-input');
      const count = this.useCount + generatedLinks.length;
      this.useIndicators.forEach((indicator, index) => {
        if (index >= count) {
          indicator.classList.remove('active');
        } else {
          indicator.classList.add('active');
        }
      });

      this.uses.innerText = count;

      if (count >= this.maxLimit) {
        this.generateLinkButtons.forEach((button) => button.setAttribute('disabled', true));
        this.emailInviteButtons.forEach((button) => button.setAttribute('disabled', true));
      } else {
        this.generateLinkButtons.forEach((button) => button.removeAttribute('disabled'));
        if (!this.emailSubmitPending) {
          this.emailInviteButtons.forEach((button) => button.removeAttribute('disabled'));
        }
      }

      if (this.submitButton) {
        if (emailInputs.length >= 2) {
          this.submitButton.innerText = window.referAFriend.strings.submitAll;
        } else {
          this.submitButton.innerText = window.referAFriend.strings.submit;
        }
      }

      this.updateEmailInviteVisibility();
    }

    buildLink(link, referralCode) {
      const element = document.createElement('refer-a-friend-link');
      element.dataset.referralCode = referralCode;
      element.classList.add('w-full', 'gap-4', 'flex');

      element.innerHTML = `
        <input class="input" type="text" value="${link}" aria-label="Referral link" readonly>
        <button class="button button--alt rounded-full h-12.5 max-h-none min-w-24" type="button">
          ${window.referAFriend.strings.copy}
        </button>
      `;

      return element;
    }

    buildEmailInput(email, removeButton) {
      const element = document.createElement('refer-a-friend-email-input');
      element.classList.add('w-full', 'gap-4', 'flex');

      element.innerHTML = `
        <input class="input" type="email" value="${email ? email : ''}" placeholder="${window.referAFriend.strings.email || 'Email'}" ${removeButton ? '' : 'readonly'}>
        ${
          removeButton
            ? `
            <button aria-label="Remove email">
              <svg class="size-4" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 1.62695L9.60449 8.11035L15.7822 14.373L14.1768 16L8 9.7373L1.82324 16L0.217773 14.373L6.39453 8.11035L0 1.62695L1.60547 0L8 6.48242L14.3945 0L16 1.62695Z" fill="currentColor"/>
              </svg>
            </button>
          `
            : ''
        }
      `;

      return element;
    }
  }

  window.customElements.define('refer-a-friend', ReferAFriend);
}

if (!window.customElements.get('refer-a-friend-link')) {
  class ReferAFriendLink extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.input = this.querySelector('input');
      this.button = this.querySelector('button');

      if (!this.input || !this.button) return;

      this.button.addEventListener('click', () => this.copyLink());
    }

    copyLink() {
      navigator.clipboard.writeText(this.input.value).then(
        () => {
          this.button.innerText = window.referAFriend.strings.copied;
          this.closest('refer-a-friend')?.trackReferralLinkCopied(this.dataset.referralCode);
          this.button.setAttribute('disabled', true);

          setTimeout(() => {
            this.button.innerText = window.referAFriend.strings.copy;
            this.button.removeAttribute('disabled');
          }, 2000);
        },
        () => {
          showReferAFriendToast('There was an error copying the link. Please try again.', 'error');
        }
      );
    }
  }

  window.customElements.define('refer-a-friend-link', ReferAFriendLink);
}

if (!window.customElements.get('refer-a-friend-email-input')) {
  class ReferAFriendEmailInput extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.referAFriendSection = this.closest('refer-a-friend');
      this.input = this.querySelector('input');
      this.button = this.querySelector('button');

      if (!this.referAFriendSection || !this.input || !this.button) return;

      this.button.addEventListener('click', () => this.handleRemove());
      this.input.setAttribute('required', true);
    }

    handleRemove() {
      this.remove();
      this.referAFriendSection.syncEmailFieldMetadata();
      this.referAFriendSection.updateUseCounts();
    }
  }

  window.customElements.define('refer-a-friend-email-input', ReferAFriendEmailInput);
}
